import { ReactNode, useCallback, useEffect, useState, useRef } from "react";

import CloseIcon from '@/assets/icons/x-icon-black.svg';
import { getTooltipPosition, tooltipPositions } from "./helpers";
import { useAppDispatch, useAppSelector } from "@/shared/hooks/store";
import { selectHelpersShown } from "@/store/slices/auth/selectors";
import { addHelperShown } from "@/store/slices/auth";

interface IProps {
    open?: boolean;
    anchorEl?: HTMLElement | null;
    content?: ReactNode;
    onClose?(): void;
    hasCloseIcon?: boolean;
    helperArrowClassName?: string;
    uniqueIdentifier?: string;
    helperTimeout?: number;
    wrapperClassName?: string;
    className?: string;
    supportClickOutside?: boolean;
    forceBelow?: boolean;
    children?: ReactNode;
    customPosition?: keyof typeof tooltipPositions;
};

export const Tooltip = ({
    className = '',
    wrapperClassName = '',
    helperTimeout = 6000,
    uniqueIdentifier,
    open,
    content,
    anchorEl,
    onClose,
    children,
    customPosition,
    supportClickOutside = true,
    forceBelow = false,
    hasCloseIcon = true,
}: IProps) => {
    const [elemBounding, setElemBounding] = useState<DOMRect | null>(null);
    const toolTipRef = useRef<HTMLDivElement | null>(null);
    const helpersShown = useAppSelector(selectHelpersShown);
    const dispatch = useAppDispatch();
    const [alreadyShown, setAlreadyShown] = useState(uniqueIdentifier && helpersShown.includes(uniqueIdentifier));

    const updateBounding = useCallback(() => {
        if (anchorEl) {
            setElemBounding(anchorEl.getBoundingClientRect());
        }
    }, [anchorEl]);

    useEffect(() => {
        if (!anchorEl || !open) return;

        updateBounding();

        window.addEventListener('scroll', updateBounding, true);
        window.addEventListener('resize', updateBounding);

        const observer = new MutationObserver(updateBounding);
        observer.observe(anchorEl, {
            attributes: true,
            childList: true,
            subtree: true,
        });

        return () => {
            window.removeEventListener('scroll', updateBounding, true);
            window.removeEventListener('resize', updateBounding);
            observer.disconnect();
        };
    }, [anchorEl, updateBounding, open]);

    const close = () => {
        uniqueIdentifier && setAlreadyShown(true);
        onClose?.();
    }

    useEffect(() => {
        supportClickOutside && open && close && setTimeout(() => window.addEventListener('click', close), 0);
        return () => {
            supportClickOutside && close && window.removeEventListener('click', close);
        }
    }, [open]);

    useEffect(() => {
        if (open && uniqueIdentifier && !alreadyShown) {
            dispatch(addHelperShown(uniqueIdentifier));

            const timeout = setTimeout(() => {
                close?.();
            }, helperTimeout);

            return () => {
                timeout && clearTimeout(timeout);
            }
        }
    }, [open, uniqueIdentifier, alreadyShown, helperTimeout]);

    if (!open || !elemBounding || alreadyShown) return null;

    const offsetWidth = toolTipRef?.current?.offsetWidth;
    const { style, polygonClassName, polygonStyle } = getTooltipPosition(elemBounding, offsetWidth, forceBelow, customPosition);

    return (
        <div ref={toolTipRef} className={`${wrapperClassName} fixed`} style={style} onClick={(e) => e.stopPropagation()}>
            <span style={polygonStyle || {}} className={`absolute border-l-[10px] border-r-[10px] border-b-[20px] border-l-transparent border-r-transparent border-b-mint-400 ${polygonClassName}`} />
            <div className={`${className} max-w-[340px] relative shadow-dashboardTooltip flex w-full items-center rounded-[15px] bg-mint-400 py-[1.3125rem] px-[1.375rem] md:px-[1.75rem] md:gap-5 md:px-5 md:py-6`}>
                {children || (
                    <span className="mr-3 font-dmSans text-sm text-black md:text-base">
                        {content}
                    </span>
                )}
                {hasCloseIcon && <img src={CloseIcon} alt="" width={14} className="cursor-pointer absolute top-4 right-4" onClick={close} />}
            </div>
        </div>
    )
};

export default Tooltip;